import { useState, useEffect, forwardRef } from 'react';
import _isEmpty from 'lodash/isEmpty';

import { Modal, ModalMethods, Text, Button, toasts } from '../../../../../ui-component';
import { api } from '../../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../../utils/common';

type MeasurementParam = {
  id: number;
  name: string;
  icon_url?: string;
};

type EditMeasurementNameModalProps = {
  measurementParam: MeasurementParam | null;
  boutiqueMeasurementsId: number | null;
  categoryName?: string;
  onSaved?: () => void;
};

const EditMeasurementNameModal = forwardRef<ModalMethods, EditMeasurementNameModalProps>(
  ({ measurementParam, boutiqueMeasurementsId, categoryName, onSaved }, ref) => {
    const [name, setName] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (measurementParam) {
        setName(measurementParam.name);
      } else {
        setName('');
      }
    }, [measurementParam]);

    const handleSave = async () => {
      const trimmedName = name.trim();

      if (_isEmpty(trimmedName)) {
        toasts('error', 'Please enter a measurement name', 'measurement-name-required');

        return;
      }

      if (!measurementParam || !boutiqueMeasurementsId) {
        toasts('error', 'Invalid measurement data', 'edit-measurement-invalid');

        return;
      }

      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-missing');

        return;
      }

      setIsSaving(true);

      try {
        const response = await api.putRequest(
          `new_measurement/v2/${boutique_id}/boutique_measurements/${measurementParam.id}`,
          {
            id: measurementParam.id,
            name: trimmedName,
          }
        );

        if (response.status) {
          toasts(
            'success',
            'Measurement name updated successfully',
            'measurement-name-update-success'
          );

          if (onSaved) {
            onSaved();
          }

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }
        } else {
          toasts(
            'error',
            response.message || 'Failed to update measurement name',
            'measurement-name-update-error'
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'measurement-name-update-error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <Modal ref={ref} size="xl" title="Edit measurement name" showFooter={false} showCloseIcon>
        <div className="flex flex-col gap-4">
          {categoryName && (
            <Text className="font-inter text-xs text-gray-500" color="black">
              Selected: {categoryName}
            </Text>
          )}
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter measurement name"
            className="w-full h-10 px-4 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          />
          <Button
            label="Save Changes"
            size="medium"
            bgColor="primary"
            onClick={handleSave}
            disabled={isSaving}
          />
        </div>
      </Modal>
    );
  }
);

EditMeasurementNameModal.displayName = 'EditMeasurementNameModal';

export default EditMeasurementNameModal;
